// Dashboard · Branding (white-label) form.
import React from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Palette } from "lucide-react";
import { Field } from "./primitives";

export default function BrandingTab({ brand, setBrand, saving, onSave }) {
  return (
    <Card className="card-elevated p-7">
      <div className="flex items-center gap-3 mb-2">
        <Palette className="w-5 h-5 text-accent" />
        <div>
          <div className="overline">White-label</div>
          <h3 className="font-sans-ui font-semibold text-lg mt-1">Your branded experience</h3>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-2">
        Set a subdomain plus your logo, colors and headline. Visitors arriving at <span className="text-foreground">your-subdomain.bizpulseai.net</span> see your brand and start the Business Growth Discovery™ locked to your account. Until DNS is wired, you can preview your brand at <span className="text-foreground">?tenant=&lt;subdomain&gt;</span>.
      </p>

      <div className="grid md:grid-cols-2 gap-5 mt-6">
        <Field label="Subdomain (3-32 chars, a-z, 0-9, -)">
          <div className="flex items-center gap-2">
            <Input data-testid="brand-subdomain" value={brand.subdomain}
              onChange={(e) => setBrand({ ...brand, subdomain: e.target.value.toLowerCase() })}
              placeholder="acme" />
            <span className="text-muted-foreground text-sm whitespace-nowrap">.bizpulseai.net</span>
          </div>
        </Field>
        <Field label="Logo URL (PNG/SVG, ≤180px tall)">
          <Input data-testid="brand-logo" value={brand.brand_logo_url}
            onChange={(e) => setBrand({ ...brand, brand_logo_url: e.target.value })}
            placeholder="https://yourcdn.com/logo.png" />
        </Field>
        <Field label="Primary background color (hex)">
          <div className="flex items-center gap-2">
            <Input data-testid="brand-primary" value={brand.brand_primary}
              onChange={(e) => setBrand({ ...brand, brand_primary: e.target.value })}
              placeholder="#0B101E" />
            {brand.brand_primary && <span className="w-8 h-8 rounded-md border border-border" style={{ backgroundColor: brand.brand_primary }} />}
          </div>
        </Field>
        <Field label="Accent color (hex)">
          <div className="flex items-center gap-2">
            <Input data-testid="brand-accent" value={brand.brand_accent}
              onChange={(e) => setBrand({ ...brand, brand_accent: e.target.value })}
              placeholder="#C8AD7F" />
            {brand.brand_accent && <span className="w-8 h-8 rounded-md border border-border" style={{ backgroundColor: brand.brand_accent }} />}
          </div>
        </Field>
        <div className="md:col-span-2">
          <Field label="Custom hero headline">
            <Input data-testid="brand-headline" value={brand.brand_headline}
              onChange={(e) => setBrand({ ...brand, brand_headline: e.target.value })}
              placeholder="Acme Advisory — Diagnose. Grow. Lead." />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Custom hero sub-headline">
            <Textarea data-testid="brand-subheadline" rows={3} value={brand.brand_subheadline}
              onChange={(e) => setBrand({ ...brand, brand_subheadline: e.target.value })}
              placeholder="One-paragraph value proposition for your firm." />
          </Field>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mt-7">
        {brand.subdomain && (
          <a href={`/?tenant=${brand.subdomain}`} target="_blank" rel="noreferrer" className="text-sm text-accent hover:underline" data-testid="brand-preview-link">
            Preview branded experience →
          </a>
        )}
        <Button data-testid="brand-save" onClick={onSave} disabled={saving} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-7 ml-auto">
          {saving ? "Saving…" : "Save branding"}
        </Button>
      </div>
    </Card>
  );
}
